import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {PnlData, PnlFilter, PnlRawData} from '../models/pnl';
import {ApiService} from '../../api/api.service';
import {map, switchMap} from 'rxjs/operators';
import { ChartColor, ChartData, ChartType } from 'src/app/models/chart';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PnlService {
  public static readonly ONE_DAY = 24 * 60 * 60;
  public readonly TIME_OFFSET_IN_SEC = new Date().getTimezoneOffset() * 60;
  public readonly DATA_LIMIT_WEB = 90;
  public readonly DATA_LIMIT_MOBILE = 30;

  constructor(private api: ApiService) {
  }

  private _getUtc(date) {
    return !date ? null : (new Date(date).getTime() / 1000) - this.TIME_OFFSET_IN_SEC;
  }

  private _getParsedData(data: PnlRawData[]): PnlData[] {
    return data?.map((dp: PnlRawData) => ({
      percentageCumulativePnl: parseFloat(dp.percentageCumulativePnl),
      totalProWallet: parseFloat(dp.totalProWallet),
      timestamp: parseInt(dp.timestamp, 10) * 1000,
      cumulativePnl: parseFloat(dp.cumulativePnl),
      dailyPnl: parseFloat(dp.dailyPnl),
      realizedPnl: parseFloat(dp.realizedPnl)
    })).sort((a, b) => b.timestamp - a.timestamp) || [];
  }

  getDailyPnl(inCurrency: string): Observable<PnlData> {
    const now = new Date().getTime();
    return this.getPnl(
      {
        fromDate: now,
        toDate: now,
        cryptocurrency: null
      },
      inCurrency
    ).pipe(
      map(pnlData => pnlData?.[0])
    );
  }

  mapToDailyPnlChartData(pnlData: PnlData[]): ChartData[] {
    const dailyPnlCharData = [];
    const walletValue = [];
    pnlData.forEach(data => {
      const formattedX = formatDate(data.timestamp, 'yyyy-MM-dd', 'en');

      dailyPnlCharData.push({
        x: formattedX,
        y: data.dailyPnl
      });

      walletValue.push({
        x: formattedX,
        y: data.totalProWallet
      });
    });
    return [
      {name: 'Daily PnL', data: dailyPnlCharData, type: ChartType.Column},
      {name: 'Wallet Value', data: walletValue, type: ChartType.Line}
    ];
  }

  mapToCumulativeChartData(pnlData: PnlData[]): ChartData[] {
    const cumulativePnlData = [];
    pnlData.forEach(data => {
      const formattedX = formatDate(data.timestamp, 'yyyy-MM-dd', 'en');
      cumulativePnlData.push({
        x: formattedX,
        y: data.cumulativePnl,
      });
    });

    return [
      {name: 'Cumulative PnL', data: cumulativePnlData, type: ChartType.Line}
    ];
  }

  mapToRealizedPnlPoints(pnlData: PnlData[]) {
    const points = pnlData.filter(d => !!d.realizedPnl).map(
      dp => ({
        x: dp.timestamp,
        y: dp.cumulativePnl,
        value: dp.realizedPnl,
        marker: {
          size: 3,
          fillColor: '#fff',
          strokeColor: dp.realizedPnl < 0 ? ChartColor.Negative : ChartColor.Positive,
          radius: 2,
          cssClass: 'apexcharts-custom-class'
        },
        label: {
          borderColor: '#FF4560',
          offsetY: 0,
          style: {
            color: '#fff',
            background: '#FF4560',
          },

          text: '',
        }
      })
    );
    return {
      points,
      images: [],
    };
  }

  getPnl(pnlFilter: Partial<PnlFilter>, inCurrency: string): Observable<PnlData[]> {

    const vars = {
      ...pnlFilter,
      fromDate: this._getUtc(pnlFilter.fromDate),
      toDate: this._getUtc(pnlFilter.toDate),
      incurrency: inCurrency
    };

    return this.api.get(
      `
        query ($incurrency: String!, $cryptocurrency: String, $fromDate: Timestamp!, $toDate: Timestamp!) {
          totalPnlInCurrency(
            cryptocurrency: $cryptocurrency,
            fromDate: $fromDate,
            toDate: $toDate,
            incurrency: $incurrency,
          ) {
            pnlData {
              cumulativePnl
              dailyPnl
              percentageCumulativePnl
              timestamp
              totalProWallet
              realizedPnl
            }
          }
        }
      `,
      vars
    ).pipe(
      switchMap((response) => {
        if (response['errors']) {
          return throwError(response);
        }

        const parsedResponse = this._getParsedData(response['data']?.['totalPnlInCurrency']?.['pnlData']);

        return of(parsedResponse);
      }),
      this.api.unifyErrors()
    );
  }
}
